import { useState } from 'react';
import { alertOptions } from '../constants';

const LINKED_OPS_BASE = 'Transfers above 50 000 EUR for linked operations in 48 hours';
const CUSTOMER_COUNTRY_LIST_BASE = 'Customer country list';

export function useAlerts() {
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>([]);
  const [linkedOpsAmount, setLinkedOpsAmount] = useState<number>(50000);
  const [linkedOpsHours, setLinkedOpsHours] = useState<number>(48);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  function handleAlertChange(alert: string, isSelected: boolean): void {
    setSelectedAlerts(prev => {
      // Special handling for linked operations alert: inject dynamic values
      if (alert === LINKED_OPS_BASE) {
        const dynamicText = `Transfers above ${linkedOpsAmount} EUR for linked operations in ${linkedOpsHours} hours`;
        if (isSelected) {
          // Remove any previous dynamic versions first, then add
          const filtered = prev.filter(a => !a.startsWith('Transfers above') || !a.includes('linked operations'));
          return [...filtered, dynamicText];
        } else {
          return prev.filter(a => a !== dynamicText);
        }
      }
      // Special handling for customer country list: only add if countries are selected
      if (alert === CUSTOMER_COUNTRY_LIST_BASE) {
        if (isSelected && selectedCountries.length > 0) {
          const countryText = `Customer country list: ${selectedCountries.join(', ')}`;
          return [...prev.filter(a => !a.startsWith('Customer country list')), countryText];
        } else {
          return prev.filter(a => !a.startsWith('Customer country list'));
        }
      }
      if (isSelected) {
        return [...prev, alert];
      }
      return prev.filter(a => a !== alert);
    });
  }

  function updateLinkedOpsAmount(amount: number): void {
    setLinkedOpsAmount(amount);
    // If selected, update the selectedAlerts entry live
    setSelectedAlerts(prev => {
      const hasLinked = prev.some(a => a.startsWith('Transfers above') && a.includes('linked operations'));
      if (!hasLinked) return prev;
      return prev.map(a => (a.startsWith('Transfers above') && a.includes('linked operations')) 
        ? `Transfers above ${amount} EUR for linked operations in ${linkedOpsHours} hours` 
        : a);
    });
  }

  function updateLinkedOpsHours(hours: number): void {
    setLinkedOpsHours(hours);
    // If selected, update the selectedAlerts entry live
    setSelectedAlerts(prev => {
      const hasLinked = prev.some(a => a.startsWith('Transfers above') && a.includes('linked operations'));
      if (!hasLinked) return prev;
      return prev.map(a => (a.startsWith('Transfers above') && a.includes('linked operations')) 
        ? `Transfers above ${linkedOpsAmount} EUR for linked operations in ${hours} hours` 
        : a);
    });
  }

  function handleCountriesChange(countries: string[]): void {
    setSelectedCountries(countries);
    // Automatically add/update the alert when countries are selected, or remove when empty
    setSelectedAlerts(prev => {
      if (countries.length > 0) {
        const countryText = `Customer country list: ${countries.join(', ')}`;
        return [...prev.filter(a => !a.startsWith('Customer country list')), countryText];
      } else {
        return prev.filter(a => !a.startsWith('Customer country list'));
      }
    });
  }

  return {
    selectedAlerts,
    linkedOpsAmount,
    linkedOpsHours,
    selectedCountries,
    alertOptions,
    LINKED_OPS_BASE,
    CUSTOMER_COUNTRY_LIST_BASE,
    handleAlertChange,
    updateLinkedOpsAmount,
    updateLinkedOpsHours,
    handleCountriesChange,
    setSelectedAlerts
  };
}

